/**
 * Device Token Storage
 *
 * Manages the "Remember this device" token in localStorage.
 * The token is a signed JWT issued by the VerifyAuthChallenge Lambda
 * after successful WebAuthn MFA.
 *
 * Security notes:
 * - Token is HMAC-SHA256 signed (tamper-proof)
 * - 30-day expiry (configurable server-side)
 * - Bound to specific user (sub claim)
 * - Stored in localStorage (survives browser restarts)
 * - Vulnerable to XSS — acceptable tradeoff for UX
 *   (XSS can steal session tokens too, so this doesn't add new risk)
 * - Token is sent as clientMetadata on auth, not as a cookie
 *   (no CSRF risk)
 */

const DEVICE_TOKEN_KEY = "affektra.device_token";

/**
 * Store the device token after successful MFA.
 */
export function saveDeviceToken(token: string): void {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch (e) {
    console.warn("[device-token] Failed to save:", e);
  }
}

/**
 * Retrieve the stored device token.
 * Returns null if no token is stored.
 */
export function getDeviceToken(): string | null {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the device token (e.g., on logout or manual "forget device").
 */
export function clearDeviceToken(): void {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/**
 * Check if the device token is likely valid (basic client-side check).
 * This does NOT verify the signature — that's done server-side.
 * This only checks if the token exists and its exp claim hasn't passed.
 */
export function hasValidDeviceToken(): boolean {
  const token = getDeviceToken();
  if (!token) return false;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    // Decode payload (base64url)
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(decoded);

    // Check expiry
    if (claims.exp && claims.exp < Math.floor(Date.now() / 1000)) {
      clearDeviceToken(); // Clean up expired token
      return false;
    }

    return true;
  } catch {
    return false;
  }
}
